import { useState } from "react";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FitScreen as FitScreenIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  RotateRight as RotateRightIcon,
  Fullscreen as FullscreenIcon,
  PanTool as PanToolIcon,
  RestartAlt as RestartAltIcon,
  AspectRatio as AspectRatioIcon,
  CropFree as CropFreeIcon,
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
export function CompactViewerHeader({
  fileName,
  onClose,
  currentPage = 1,
  totalPages = 1,
  onPreviousPage,
  onNextPage,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  minZoom = 50,
  maxZoom = 200,
  onInfoClick,
  onPrint,
  onShare,
  onRotate,
  onFullscreen,
  onFitToWidth,
  onFitToPage,
  onPanMode,
  onResetView,
  onActualSize,
  onZoomToFit,
  showPageNav = false,
  isPanMode = false,
  isFullscreen = false,
  darkMode = false,
  // React PDF Viewer page navigation components
  GoToPreviousPage,
  GoToNextPage,
  CurrentPageLabel,
  NumberOfPages,
}) {
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const handleMoreClick = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreMenuAnchor(null);
  };
  const handleMenuItemClick = (action) => {
    action();
    handleMoreClose();
  };
  return (
    <Box
      sx={{
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1,
        gap: 1,
        borderBottom: darkMode ? "1px solid #4A5565" : "1px solid #E5E7EB",
        bgcolor: darkMode ? "#212121" : "#F2F2F2",
        flexShrink: 0,
      }}
    >
      {/* Left Section: Close + Filename */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: "0 1 auto",
        }}
      >
        {onClose && (
          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
                padding: "4px",
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            color: darkMode ? "#FFFFFF" : "#212121",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: {
              xs: "120px",
              sm: "200px",
              md: "300px",
            },
          }}
        >
          {fileName}
        </Typography>
      </Box>

      {/* Center Section: Page Navigation + Zoom Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flex: "0 0 auto",
        }}
      >
        {/* Page Navigation (PDF only) */}
        {showPageNav && (
          <>
            <Tooltip title="Previous page">
              <span>
                <IconButton
                  onClick={onPreviousPage}
                  disabled={currentPage <= 1 || totalPages === 0}
                  size="small"
                  sx={{
                    color: darkMode ? "#FFFFFF" : "#4A5565",
                    "&:hover": {
                      bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                    },
                    "&:disabled": {
                      color: "#E5E7EB",
                    },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: darkMode ? "#E5E7EB" : "#4A5565",
                minWidth: "60px",
                textAlign: "center",
                display: {
                  xs: "none",
                  sm: "block",
                },
              }}
            >
              {currentPage} / {totalPages > 0 ? totalPages : "..."}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: darkMode ? "#E5E7EB" : "#4A5565",
                minWidth: "40px",
                textAlign: "center",
                display: {
                  xs: "block",
                  sm: "none",
                },
              }}
            >
              {currentPage}/{totalPages > 0 ? totalPages : "..."}
            </Typography>

            <Tooltip title="Next page">
              <span>
                <IconButton
                  onClick={onNextPage}
                  disabled={currentPage >= totalPages || totalPages === 0}
                  size="small"
                  sx={{
                    color: darkMode ? "#FFFFFF" : "#4A5565",
                    "&:hover": {
                      bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                    },
                    "&:disabled": {
                      color: "#E5E7EB",
                    },
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                mx: 0.5,
                bgcolor: darkMode ? "#4A5565" : undefined,
              }}
            />
          </>
        )}

        {/* Zoom Controls */}
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              onClick={onZoomOut}
              disabled={zoom <= minZoom}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
                "&:disabled": {
                  color: "#E5E7EB",
                },
              }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          onClick={onZoomReset}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "13px",
            color: darkMode ? "#E5E7EB" : "#4A5565",
            minWidth: {
              xs: "35px",
              sm: "45px",
            },
            textAlign: "center",
            cursor: onZoomReset ? "pointer" : "default",
            "&:hover": onZoomReset
              ? {
                  color: "#2932E5",
                }
              : {},
            userSelect: "none",
          }}
        >
          {zoom}%
        </Typography>

        <Tooltip title="Zoom in">
          <span>
            <IconButton
              onClick={onZoomIn}
              disabled={zoom >= maxZoom}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
                "&:disabled": {
                  color: "#E5E7EB",
                },
              }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            mx: 0.5,
            bgcolor: darkMode ? "#4A5565" : undefined,
          }}
        />

        {/* Pan Mode */}
        {onPanMode && (
          <Tooltip title={isPanMode ? "Exit pan mode" : "Pan mode (Space)"}>
            <IconButton
              onClick={onPanMode}
              size="small"
              sx={{
                color: isPanMode ? "#2932E5" : darkMode ? "#FFFFFF" : "#4A5565",
                bgcolor: isPanMode ? "#FFE0D0" : "transparent",
                "&:hover": {
                  bgcolor: isPanMode
                    ? "#FFE0D0"
                    : darkMode
                    ? "#4A5565"
                    : "#E5E7EB",
                },
              }}
            >
              <PanToolIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Actual Size */}
        {onActualSize && (
          <Tooltip title="Actual size (Ctrl+1)">
            <IconButton
              onClick={onActualSize}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
              }}
            >
              <AspectRatioIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Zoom to Fit */}
        {onZoomToFit && (
          <Tooltip title="Zoom to fit (Ctrl+0)">
            <IconButton
              onClick={onZoomToFit}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
              }}
            >
              <CropFreeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Reset View */}
        {onResetView && (
          <Tooltip title="Reset view">
            <IconButton
              onClick={onResetView}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
              }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Fit Controls */}
        {(onFitToWidth || onFitToPage) && (
          <Tooltip title="Fit to screen">
            <IconButton
              onClick={onFitToPage || onFitToWidth}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
                display: {
                  xs: "none",
                  md: "inline-flex",
                },
              }}
            >
              <FitScreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Right Section: Fullscreen + Info + More */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flex: "0 0 auto",
        }}
      >
        {/* Fullscreen Button - Moved from 3-dot menu for better discoverability */}
        {onFullscreen && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                mx: 0.5,
                bgcolor: darkMode ? "#4A5565" : undefined,
              }}
            />
            <Tooltip title="Fullscreen (F)">
              <IconButton
                onClick={onFullscreen}
                size="small"
                sx={{
                  color: isFullscreen
                    ? "#2932E5"
                    : darkMode
                    ? "#FFFFFF"
                    : "#4A5565",
                  bgcolor: isFullscreen ? "#FFE0D0" : "transparent",
                  "&:hover": {
                    bgcolor: isFullscreen
                      ? "#FFE0D0"
                      : darkMode
                      ? "#4A5565"
                      : "#E5E7EB",
                  },
                }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {onInfoClick && (
          <Tooltip title="Info">
            <IconButton
              onClick={onInfoClick}
              size="small"
              sx={{
                color: darkMode ? "#FFFFFF" : "#4A5565",
                "&:hover": {
                  bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
                },
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="More options">
          <IconButton
            onClick={handleMoreClick}
            size="small"
            sx={{
              color: darkMode ? "#FFFFFF" : "#4A5565",
              "&:hover": {
                bgcolor: darkMode ? "#4A5565" : "#E5E7EB",
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* More Menu */}
        <Menu
          anchorEl={moreMenuAnchor}
          open={Boolean(moreMenuAnchor)}
          onClose={handleMoreClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              borderRadius: "8px",
              minWidth: "180px",
            },
          }}
        >
          {onPrint && (
            <MenuItem onClick={() => handleMenuItemClick(onPrint)}>
              <ListItemIcon>
                <PrintIcon
                  fontSize="small"
                  sx={{
                    color: "#4A5565",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Print"
                primaryTypographyProps={{
                  sx: {
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#212121",
                  },
                }}
              />
            </MenuItem>
          )}

          {onShare && (
            <MenuItem onClick={() => handleMenuItemClick(onShare)}>
              <ListItemIcon>
                <ShareIcon
                  fontSize="small"
                  sx={{
                    color: "#4A5565",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Share"
                primaryTypographyProps={{
                  sx: {
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#212121",
                  },
                }}
              />
            </MenuItem>
          )}

          {onRotate && (
            <MenuItem onClick={() => handleMenuItemClick(onRotate)}>
              <ListItemIcon>
                <RotateRightIcon
                  fontSize="small"
                  sx={{
                    color: "#4A5565",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Rotate"
                primaryTypographyProps={{
                  sx: {
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#212121",
                  },
                }}
              />
            </MenuItem>
          )}

          {/* Fullscreen removed from menu - now in header bar for better discoverability */}

          {(onFitToWidth || onFitToPage) && (
            <MenuItem
              onClick={() =>
                handleMenuItemClick(onFitToPage || onFitToWidth || (() => {}))
              }
              sx={{
                display: {
                  xs: "flex",
                  md: "none",
                },
              }}
            >
              <ListItemIcon>
                <FitScreenIcon
                  fontSize="small"
                  sx={{
                    color: "#4A5565",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Fit to screen"
                primaryTypographyProps={{
                  sx: {
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#212121",
                  },
                }}
              />
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
}
